import { i18nMark, Trans } from 'lingui-react';
import { filter, get, uniq } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { compose, setDisplayName, branch } from 'recompose';

import { Accordion, Badge, P, Text } from 'base-components';
import { px2rem } from 'decisiv-ui-utils';
import { Column, Container, Row } from 'styled-components-reactive-grid';

import withCustomerSelect from '../../withCustomerSelect';
import withStoreCustomerSelect from '../../withStoreCustomerSelect';
import withEmailNotificationPreferences from './withEmailNotificationPreferences';

function NotificationPreference({ type, emailNotificationPreferences }) {
  const isActive = emailNotificationPreferences.length > 0;

  return (
    <Row modifiers="padScaleY_0">
      <Column>
        <Badge modifiers={[isActive && 'info']}>
          <Badge.Icon name={isActive ? 'envelope-check' : 'envelope-times'} />
        </Badge>
      </Column>
      <Column modifiers={['col', 'padScaleY_1']}>
        <Row modifiers="padScaleY_0">
          <Column modifiers="padScaleY_0">
            <Text>
              <Trans id={type.title} />
            </Text>
          </Column>
        </Row>
        <Row>
          <Column modifiers={['col', 'padScaleY_0']}>
            <Text modifiers={['small', 'textLight']}>
              <Trans id={type.description} />
            </Text>
          </Column>
        </Row>
        <Row modifiers="padScaleY_1">
          <Column>
            {emailNotificationPreferences.map(pref => (
              <P modifiers={['small', 'textLight']} key={`email - ${pref}`}>
                {pref}
              </P>
            ))}
          </Column>
        </Row>
      </Column>
    </Row>
  );
}

NotificationPreference.propTypes = {
  emailNotificationPreferences: PropTypes.arrayOf(PropTypes.string.isRequired)
    .isRequired,
  type: PropTypes.shape({
    description: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
};

const NotificationTypes = {
  dispatched: {
    title: i18nMark('Dispatched'),
    description: i18nMark(
      'Receive notification when breakdown has been dispatched.',
    ),
  },
  rolling: {
    title: i18nMark('Vehicle Rolling'),
    description: i18nMark(
      'Receive notification when the service provider technician has vehicle rolling.',
    ),
  },
};

function EmailNotifications({ emailNotificationPreferences, customer }) {
  const dispatchedPreferences = uniq(
    filter(
      emailNotificationPreferences,
      ({ notifyWhenDispatched, email }) => !!notifyWhenDispatched && !!email,
    ).map(pref => pref.email),
  );

  const rollingPreferences = uniq(
    filter(
      emailNotificationPreferences,
      ({ notifyWhenRolling, email }) => !!notifyWhenRolling && !!email,
    ).map(pref => pref.email),
  );

  const hasData = !!dispatchedPreferences.length || !!rollingPreferences.length;

  return (
    <Accordion
      key={get(customer, 'id')}
      disabled={!hasData}
      defaultExpanded={false}
    >
      <Accordion.Head>
        <Accordion.Title>
          <Trans>Email Notifications</Trans>
        </Accordion.Title>
      </Accordion.Head>
      {hasData && (
        <Accordion.Body>
          <Container style={{ paddingBottom: px2rem(20) }}>
            <NotificationPreference
              type={NotificationTypes.dispatched}
              emailNotificationPreferences={dispatchedPreferences}
            />
            <NotificationPreference
              type={NotificationTypes.rolling}
              emailNotificationPreferences={rollingPreferences}
            />
          </Container>
        </Accordion.Body>
      )}
    </Accordion>
  );
}

EmailNotifications.propTypes = {
  emailNotificationPreferences: PropTypes.arrayOf(
    PropTypes.shape({
      email: PropTypes.string,
      notifyWhenDispatched: PropTypes.bool.isRequired,
      notifyWhenRolling: PropTypes.bool.isRequired,
    }),
  ).isRequired,
  customer: PropTypes.shape({ id: PropTypes.string }),
};

EmailNotifications.defaultProps = {
  customer: undefined,
};

const isNationalCustomer = ({ customerType, customerTypes }) =>
  customerType === customerTypes.NATIONAL.type;

export default compose(
  setDisplayName('EmailNotifications'),
  /**
   * Order is important here. `withEmailNotificationPreferences`
   * needs the current customer as a prop, so we must run
   * `withCustomerSelect/withStoreCustomerSelect` first.
   */
  branch(isNationalCustomer, withCustomerSelect, withStoreCustomerSelect),
  withEmailNotificationPreferences,
)(EmailNotifications);



// WEBPACK FOOTER //
// ./src/compositions/CustomerWidget/CustomerDetails/EmailNotifications/index.js